import { type FC, useState } from "react";
import { Check, Tag } from "lucide-react";

import { cn } from "@/shared/utils/core";
import logoFull from "@/shared/assets/logo/logo-full.svg";
import { AddonPlanLayout } from "@/shared/components/AddonPlanLayout";
import type { AddonItem, OrderSummaryItem } from "@/shared/components/AddonPlanLayout";

import type { LawyerPlanKey } from "@/shared/constants/lawyerPlans";

import { BASE_PLAN, ADDONS, toggleAddonSelection, calculateTotal } from "../clientInvitePlanData";

interface ClientInviteAddonsStepProps {
  recommendedPlan: LawyerPlanKey | null;
  lawyerName: string;
  firstClientOffer: boolean;
  onContinue: (selectedAddons: LawyerPlanKey[]) => void;
}

export const ClientInviteAddonsStep: FC<ClientInviteAddonsStepProps> = ({
  recommendedPlan,
  lawyerName,
  firstClientOffer,
  onContinue,
}) => {
  const [selected, setSelected] = useState<Set<LawyerPlanKey>>(() => {
    const initial = new Set<LawyerPlanKey>();
    if (recommendedPlan === "asset_discovery") {
      initial.add("asset_discovery");
    } else if (recommendedPlan === "estate_administration") {
      initial.add("asset_discovery");
      initial.add("estate_administration");
    }
    return initial;
  });

  const isAdminSelected = selected.has("estate_administration");
  const basePrice = BASE_PLAN.price;

  const isAddonCovered = (key: LawyerPlanKey) => firstClientOffer && key === "asset_discovery";

  const FIRST_CLIENT_DISCOUNT = 3000;
  const subtotal = calculateTotal(basePrice, selected);
  const total = firstClientOffer ? Math.max(0, subtotal - FIRST_CLIENT_DISCOUNT) : subtotal;

  const topCard = (
    <div className="flex items-start gap-4 rounded-lg border border-primary bg-white p-6">
      <div className="flex size-6 shrink-0 items-center justify-center rounded-full bg-brand">
        <Check className="h-4 w-4 text-white" aria-hidden="true" />
      </div>
      <div className="flex flex-1 flex-col gap-3">
        <div className="flex flex-col gap-1">
          <div className="flex items-start gap-2">
            <span className="flex-1 text-base font-semibold">{BASE_PLAN.name}</span>
            {firstClientOffer ? (
              <>
                <span className="shrink-0 whitespace-nowrap text-base text-[#a6a09b] line-through [text-decoration-skip-ink:none]">
                  + ${basePrice.toLocaleString()}
                </span>
                <span className="shrink-0 whitespace-nowrap text-base font-semibold">$0</span>
              </>
            ) : (
              <span className="shrink-0 text-base font-semibold">
                ${basePrice.toLocaleString()}
              </span>
            )}
          </div>
          <p className="text-sm text-secondary">{BASE_PLAN.description}</p>
        </div>
        <div className="flex items-center gap-2">
          <BASE_PLAN.savingsIcon className="h-4 w-4 shrink-0 text-brand" aria-hidden="true" />
          <span className="text-sm font-medium text-brand">{BASE_PLAN.savingsText}</span>
        </div>
      </div>
    </div>
  );

  const addonItems: AddonItem<LawyerPlanKey>[] = ADDONS.map((addon) => {
    const isDisabled = addon.key === "asset_discovery" && isAdminSelected;
    const isRecommended = addon.key === recommendedPlan;
    const isDiscounted = isAddonCovered(addon.key);

    const displayPrice = isDiscounted ? (
      <>
        <span className="shrink-0 whitespace-nowrap text-base text-[#a6a09b] line-through [text-decoration-skip-ink:none]">
          + ${addon.addOnPrice.toLocaleString()}
        </span>
        <span className="shrink-0 whitespace-nowrap text-base font-semibold">+ $0</span>
      </>
    ) : undefined;

    const badges =
      isDisabled || isRecommended ? (
        <>
          {isDisabled && (
            <span className="rounded-full bg-background px-2 py-0.5 text-2xs font-medium text-secondary">
              Included in Estate administration
            </span>
          )}
          {isRecommended && (
            <span
              className={cn(
                "rounded-full px-2 py-0.5 text-2xs font-medium text-brand",
                "bg-[#e1f3d8]",
              )}
            >
              Plan recommended by {lawyerName}
            </span>
          )}
        </>
      ) : undefined;

    return {
      key: addon.key,
      name: addon.name,
      price: addon.addOnPrice,
      displayPrice,
      description: addon.description,
      savingsIcon: addon.savingsIcon,
      savingsText: addon.savingsText,
      disabled: isDisabled,
      badges,
    };
  });

  const orderSummaryItems: OrderSummaryItem[] = [
    {
      key: "estate_setup",
      label: "Estate setup",
      displayPrice: firstClientOffer ? (
        <>
          <span className="shrink-0 whitespace-nowrap text-2xs text-[#a6a09b] line-through [text-decoration-skip-ink:none]">
            ${basePrice.toLocaleString()}
          </span>
          <span className="shrink-0 whitespace-nowrap font-semibold">$0</span>
        </>
      ) : (
        <span className="shrink-0 whitespace-nowrap font-semibold">
          ${basePrice.toLocaleString()}
        </span>
      ),
    },
    ...ADDONS.filter((addon) => selected.has(addon.key)).map((addon) => {
      const addonDiscounted = isAddonCovered(addon.key);
      return {
        key: addon.key,
        label: addon.name,
        displayPrice: addonDiscounted ? (
          <>
            <span className="shrink-0 whitespace-nowrap text-2xs text-[#a6a09b] line-through [text-decoration-skip-ink:none]">
              ${addon.addOnPrice.toLocaleString()}
            </span>
            <span className="shrink-0 whitespace-nowrap font-semibold">+ $0</span>
          </>
        ) : (
          <span className="shrink-0 whitespace-nowrap font-semibold">
            + ${addon.addOnPrice.toLocaleString()}
          </span>
        ),
      };
    }),
  ];

  const sidebarExtra = firstClientOffer ? (
    <div className="inline-flex items-center gap-2 rounded-sm bg-[#f0f8ec] px-2 py-1">
      <Tag className="h-3 w-3 shrink-0 text-brand" aria-hidden="true" />
      <span className="text-2xs text-brand">{lawyerName} code applied</span>
    </div>
  ) : undefined;

  return (
    <div className="flex h-screen bg-subtle">
      <div className="relative flex flex-1 flex-col items-center overflow-y-auto p-4 sm:p-10 lg:p-16">
        <img src={logoFull} alt="Elayne" className="absolute left-4 top-4 h-8 sm:left-8 sm:top-8" />

        <div className="mt-12 flex w-full max-w-[900px] flex-col items-center gap-6 sm:mt-0 sm:gap-10">
          <div className="flex w-full max-w-[800px] flex-col gap-2 text-center">
            <h2>
              {recommendedPlan
                ? `${lawyerName} recommended ${
                    recommendedPlan === "asset_discovery"
                      ? "Asset search"
                      : recommendedPlan === "estate_administration"
                        ? "Estate administration"
                        : "Estate setup"
                  }`
                : "Choose your level of support"}
            </h2>
            <p className="text-sm text-secondary">
              {recommendedPlan
                ? "See everything Elayne offers, in case the estate's needs have changed"
                : "Most families pay from the estate, not out of pocket"}
            </p>
          </div>

          <AddonPlanLayout<LawyerPlanKey>
            topCard={topCard}
            addons={addonItems}
            selected={selected}
            onToggle={(key) => setSelected((prev) => toggleAddonSelection(prev, key))}
            orderSummaryItems={orderSummaryItems}
            sidebarExtra={sidebarExtra}
            total={total}
            footerNote="An eligible estate expense. The estate pays, now or later."
            onContinue={() => onContinue([...selected])}
          />
        </div>
      </div>
    </div>
  );
};
